import { SettingsService } from '@/services/settings'
import type {
  AccordionContent,
  FaqAccordionContent,
  FaqPageItem,
  InstantWinDraw,
  Prize,
} from '@/types'
import {
  FaqCategories,
  FaqSlugs,
  Icons,
  PrizeCategories,
} from '@/utils/helper-objects'
import { ThemeType } from '@goodlifeplus/themes'
import { defineStore } from 'pinia'

const SUBSCRIPTION_HIDDEN_FAQ_SLUGS = new Set<string>([
  FaqSlugs.InstantWinDraws,
  FaqSlugs.DailyPlayLoyalty,
  FaqSlugs.DepositsWithdrawals,
  FaqSlugs.CreditBackSiteCredit,
  FaqSlugs.WeeklyRaceGoodLifePoints,
])

export const useFaqStore = defineStore('faq', {
  getters: {
    isSubscription(): boolean {
      return this.$theme.type === ThemeType.Subscription
    },
    isCanadianLocale(): boolean {
      return import.meta.env.GLP_LOCALE === 'en-CA'
    },
    getCurrencyValue() {
      return (amount: number): number => {
        return this.isCanadianLocale ? amount * 2 : amount
      }
    },
    businessPhoneUnformatted(): string {
      return this.BUSINESS_PHONE?.replaceAll(' ', '')
    },
    contactLinkHtml(): string {
      return this.isCanadianLocale
        ? `<a href="mailto:${this.BUSINESS_EMAIL}" class="link" target="_blank">${this.BUSINESS_EMAIL}</a>`
        : `<a href="tel:${this.businessPhoneUnformatted}" class="link" target="_blank">${this.BUSINESS_PHONE}</a>`
    },
    homepageFaq(): FaqAccordionContent {
      return {
        title: '',
        content: [
          {
            title: `What is ${this.BUSINESS_NAME} and how does it work?`,
            text: `${this.BUSINESS_NAME} is a membership that gives you automatic entries into daily prize draws for luxury cars, holidays, and cash prizes. Plus, you get exclusive discounts, freebies, and perks every month.`,
          },
          {
            title: 'How do I enter the prize draws?',
            text: `Every ${this.BUSINESS_NAME} member is automatically entered into 30+ prize draws each month. You can also enter for free online or by post, check our terms for details: <a
              href="${this.$router.resolve({ name: 'terms-and-conditions' }).href}"
              class="link"
              target="_blank"
            >${this.$toFullUrl('terms-and-conditions')}</a>.`,
          },
          {
            title: `Is ${this.BUSINESS_NAME} really free to enter?`,
            text: 'Yes! You can enter each prize draw for free online or via post. However, becoming a member gives you multiple automatic entries and unlocks exclusive perks.',
          },
          {
            title: 'What prizes can I win?',
            text: 'Luxury cars, dream holidays, tech gadgets, cash prizes, and more! We run daily prize draws. Check our homepage for upcoming prizes.',
          },
          {
            title: 'Can I cancel my membership at any time?',
            text: 'Yes, you can cancel anytime by calling our support team, no hidden fees or contracts.',
          },
        ],
      }
    },
    campaignFaq(): FaqAccordionContent {
      return {
        title: 'Your questions, answered',
        content: [
          {
            title: `What is ${this.BUSINESS_NAME}?`,
            text: `${this.BUSINESS_NAME} is an entertainment focussed membership, which provides automatic entry to daily draws, a range of monthly perks ranging from cinema tickets to discounts and deals. Each membership tier comes with its own number of daily entries and perks. <br>You can find out more detail on our membership page, just visit <a class='link' target='_blank' href='${this.$router.resolve({ name: 'memberships' }).href}'>${this.$toFullUrl('memberships')}</a>`,
          },
          {
            title: 'How do I know if I have won?',
            text: `Winners are announced daily during our live streams. Tune in live or visit the <a href='${this.$router.resolve({ name: 'results-and-winners' }).href}' class='link'>Winners</a> section of our website to catch up on any missed draws.<br>If you win, we'll contact you directly via the details you've provided. Don't worry - we'll always make sure you know about your win!`,
          },
          {
            title: 'How can I cancel my subscription?',
            text: `Easily! You can cancel your ${this.BUSINESS_NAME} membership at any time. Simply contact us ${this.isCanadianLocale ? 'at' : 'on'} ${this.contactLinkHtml} during our opening hours of 9.00am to 5.30pm Monday to Thursday and Friday 9.00am to 5.00pm. Please rest assured that our team will always get back to you and resolve whatever issue you may have.`,
          },
          {
            title: 'How do I enter the draws?',
            text: `There are three simple ways to enter a draw:<br><br><strong>Free online entry</strong><br>A one-time free entry for everyone. <br><br><strong>Membership</strong><br>Enjoy multiple entries, exclusive perks, and incredible rewards like dining discounts and free cinema tickets — all starting at just ${this.$i18n.n(11.99, 'currency')} a month.<br><br><strong>Postal entry</strong><br>Check out the terms and conditions for details on how to enter by post. Once entered, sit back and relax or tune into our live streams to see if you've won!`,
          },
          {
            title: 'Where are you based?',
            text: `We are London based, Our dedicated team is here to support you every step of the way. Whether you have questions about our services or need assistance, you can count on us to provide prompt and helpful responses.<br>To find out more you can contact us via <a class='link' href='mailto:${this.BUSINESS_EMAIL}' target='_blank'>${this.BUSINESS_EMAIL}</a> or give our friendly team a call on <a href="tel:${this.businessPhoneUnformatted}" class="link" target="_blank">${this.BUSINESS_PHONE}</a>.`,
          },
          {
            title: 'How do benefits work?',
            text: `Each plan comes with varying benefits which you can see on <a class='link' target='_blank' href='${this.$router.resolve({ name: 'memberships' }).href}'>${this.$toFullUrl('memberships')}</a>. Your benefits are accessible to you through the memberzone; as long as your membership fee has been paid.`,
          },
        ],
      }
    },
    productFaq(): FaqAccordionContent {
      return {
        title: '',
        content: [
          {
            title: `What is ${this.BUSINESS_NAME}?`,
            text: `${this.BUSINESS_NAME} is an entertainment focussed membership, which provides automatic entry to daily draws, a range of monthly perks ranging from cinema tickets to discounts and deals. Each membership tier comes with its own number of daily entries and perks. <br>You can find out more detail on our membership page, just visit <a class='link' target='_blank' href='${this.$router.resolve({ name: 'memberships' }).href}'>${this.$toFullUrl('memberships')}</a>`,
          },
          {
            title: 'How do I know if I have won?',
            text: `Winners are announced daily during our live streams. Tune in live or visit the <a href='${this.$router.resolve({ name: 'results-and-winners' }).href}' class='link'>Winners</a> section of our website to catch up on any missed draws.<br>If you win, we'll contact you directly via the details you've provided. Don't worry - we'll always make sure you know about your win!`,
          },
          {
            title: 'How can I cancel my subscription?',
            text: `Easily! You can cancel your ${this.BUSINESS_NAME} membership at any time. Simply contact us ${this.isCanadianLocale ? 'at' : 'on'} ${this.contactLinkHtml} during our opening hours of 9.00am to 5.30pm Monday to Thursday and Friday 9.00am to 5.00pm. Please rest assured that our team will always get back to you and resolve whatever issue you may have.`,
          },
          {
            title: 'How do I enter the draws?',
            text: `There are three simple ways to enter a draw:<br><br><strong>Free online entry</strong><br>A one-time free entry for everyone. <br><br><strong>Membership</strong><br>Enjoy multiple entries, exclusive perks, and incredible rewards like dining discounts and free cinema tickets—all starting at just ${this.$i18n.n(11.99, 'currency')} a month.<br><br><strong>Postal entry</strong><br>Check out the terms and conditions for details on how to enter by post. Once entered, sit back and relax or tune into our live streams to see if you've won!`,
          },
          {
            title: 'Where are you based?',
            text: `We are London based, Our dedicated team is here to support you every step of the way. Whether you have questions about our services or need assistance, you can count on us to provide prompt and helpful responses. <br>To find out more you can contact us via <a class='link' href='mailto:${this.BUSINESS_EMAIL}' target='_blank'>${this.BUSINESS_EMAIL}</a> or give our friendly team a call on <a href="tel:${this.businessPhoneUnformatted}" class="link" target="_blank">${this.BUSINESS_PHONE}</a>.`,
          },
          {
            title: 'How do benefits work?',
            text: `Each plan comes with varying benefits which you can see on <a class='link' target='_blank' href='${this.$router.resolve({ name: 'memberships' }).href}'>${this.$toFullUrl('memberships')}</a>. Your benefits are accessible to you through the memberzone; as long as your membership fee has been paid.`,
          },
        ],
      }
    },
    ...(import.meta.env.GLP_LOCALE === 'en-GB'
      ? {
          benefitFaq(): FaqAccordionContent {
            return {
              title: '',
              content: [
                {
                  title: 'What discounts and perks do I get?',
                  text: 'As a member, you get:<br /><ul class="list-disc"><li class="ml-8">2-for-1 dining and discounts with Tastecard</li><li class="ml-8">Monthly freebies like coffee, meal deals and shopping vouchers</li><li class="ml-8">VIP benefits (for Platinum & VIP members), including free cinema tickets</li><ul>',
                },
                {
                  title: 'How do I use my benefits?',
                  text: `Visit your <a class="link" href="${this.$router.resolve({ name: 'benefits' }).href}">benefits page</a> to find and redeem your discounts, vouchers, and free treats.`,
                },
                {
                  title: 'What is a Tastecard, and how does it work?',
                  text: `Tastecard gives you 2-for-1 meals and up to 50% off at thousands of restaurants. Your digital Tastecard is available in the <a class="link" href="${this.$router.resolve({ name: 'members-zone' }).href}">${this.$i18n.t('label.members_zone')}</a>.`,
                },
                {
                  title: 'Can I upgrade or downgrade my plan?',
                  text: `You can upgrade anytime via the banner in your <a class="link" href="${this.$router.resolve({ name: 'members-zone' }).href}">Members Zone</a>. To downgrade, just give us a call and our team will sort it for you.`,
                },
                {
                  title: 'How do I withdraw money from my account?',
                  text: `If you've won cash, you can request a withdrawal right from the Member Zone:<br /><ul class="list-disc"><li class="ml-8">Enter the amount you'd like to withdraw.</li><li class='ml-8'>Click Request withdrawal.</li><li class='ml-8'>A member of our team will be in touch within 48 hours to arrange payment.</li><li class='ml-8'>Sit tight, your funds are on the way.</li></ul><br>Withdrawals must be at least ${this.$i18n.n(this.getCurrencyValue(10), 'currency')} and can only be made from your cash balance (not bonus credit).`,
                },
              ],
            }
          },
        }
      : {}),
    goodCausesFaq(): FaqAccordionContent {
      return {
        title: 'Your questions, answered',
        content: [
          {
            title: `Does ${this.BUSINESS_NAME} support good causes or charities?`,
            text: 'Yes. We donate 10% of net proceeds from all new Standard Memberships to our charity partners.',
          },
          {
            title: 'Which charities do you support?',
            text: 'We focus on charities that support young people across the UK, including:<br/><ul class="list-disc"><li class="ml-8">Legacy Youth Zone Croydon</li><li class="ml-8">RWW Foundation</li></ul><br/>We also support young upcoming athletes on their journey to the top.',
          },
        ],
      }
    },
    prizesFaq(): FaqAccordionContent {
      return {
        title: '',
        content: [
          {
            title: 'How do I enter the prize draws?',
            text: `There are three ways to enter:<br/><br/><ul><li class="ml-8"><strong>Membership:</strong> Automatic daily entries based on your plan.</li><li class="ml-8"><strong>Free online entry:</strong> a free online route.</li><li class="ml-8"><strong>By post:</strong> enter for free by post.</li></ul><br/>Full details for each route are in our <a
                    href="${this.$router.resolve({ name: 'terms-and-conditions' }).href}" 
                    class="link"
                    target="_blank"
                    >${this.$toFullUrl('terms-and-conditions')}</a>.`,
          },
          {
            title: 'What prizes can I win?',
            text: 'Luxury cars, dream holidays, the latest tech, and cash - plus new surprises added all the time. We run daily prize draws, so there’s always something exciting up for grabs.',
          },
          {
            title: 'How are winners chosen?',
            text: 'All final draw winners are selected using www.randomdraws.co.uk, an independent and certified system that ensures fairness and transparency.',
          },
          {
            title: 'What happens if a draw doesn’t sell out?',
            text: 'Every draw has a set close date. Even if all Instant Win tickets aren’t sold, the draw still goes ahead! A winner is randomly selected from all valid purchased and free entries.',
          },
          {
            title: `When are the livestreams held?`,
            text: `<ul class="list-disc"><li class="ml-8">Monday to Friday: 7pm</li><li class="ml-8">Saturday & Sunday: 12pm.</li></ul><br/><i>Excluding Bank Holidays*</i><br/>Watch live on YouTube or Facebook, or catch up later on the website results page.`,
          },
          {
            title: 'Can I watch past draws?',
            text: 'Yes. All past draws and winner announcements are available on our YouTube and Facebook channels or on our website results page.',
          },
        ],
      }
    },
    memberZoneFaq(): FaqAccordionContent {
      return {
        title: '',
        content: [
          {
            title: `What is ${this.BUSINESS_NAME}?`,
            text: `Well, since you've already signed up, you probably have an idea—but here's the lowdown anyway! ${this.BUSINESS_NAME} is your golden ticket to daily prize draws, where you could win everything from cash to cars (yes, shiny ones). On top of that, you'll enjoy monthly perks like cinema tickets, discounts on your favorite eats, and exclusive deals to make life a little sweeter.<br>Here's how it works: Every day, we automatically enter you into our prize draws—no effort required. The higher your membership tier, the more entries you get and the better the perks. So sit back, relax, and let us do the hard work while you enjoy the benefits.`,
          },
          {
            title: 'What types of prizes can I win?',
            text: `<li class='ml-8'>Prizes include everything from cash, shiny cars, and luxury vacations to the latest tech gadgets. Every day, we host a live stream to announce the winners, and we often go the extra mile by adding a little something special to enhance the winner's experience!. Check out the live streams <a class='link' target='_blank' href='${this.$router.resolve({ name: 'results-and-winners' }).href}'>here</a>. </li>`,
          },
          {
            title: `How do I get started with ${this.BUSINESS_NAME}?`,
            text: "Getting started is simple! Sign up, choose one of our three membership plans, and you'll automatically receive entries to every prize draw.",
          },
        ],
      }
    },
    liveStreamsFaq(): FaqAccordionContent {
      return {
        title: '',
        content: [
          {
            title: "How do I know if I've won a prize?",
            text: `At ${this.BUSINESS_NAME}, we believe in making our prize draws as fun and transparent as possible. Winners are announced daily during our live streams, which are an exciting part of our community experience. Even if you miss the live stream, you can still check the <a href='${this.$router.resolve({ name: 'results-and-winners' }).href}' class='link'>Winners</a> section of our website to catch up on the results.<br>If you're a lucky winner, there's no need to worry about missing out—we'll reach out to you directly using the contact details you provided when signing up. Whether it's through email, phone, or another method, we'll ensure you're promptly informed about your win so you can claim your prize without delay.`,
          },
          {
            title: 'When and where can I watch the live draws?',
            text: `Our live draws are streamed every day to keep the excitement going! You can watch them directly on our website in the <a class='link' href='${this.$router.resolve({ name: 'results-and-winners' }).href}'>Winners</a> section. These streams are a fantastic way to join in the anticipation, hear the winner announcements live, and even participate in special giveaways we sometimes offer to viewers during the broadcasts.<br>If you can't tune in live, don't worry! All the draws are saved and available for replay, so you can watch them at your convenience. The live draws are designed to be engaging, so even if you're not a winner, you'll still enjoy the thrill of seeing others celebrate their incredible prizes.`,
          },
          {
            title: 'How are the winners selected?',
            text: "To ensure complete fairness and transparency, we partner with <a class='link' target='_blank' href='//randomdraws.co.uk'>RandomDraws.co.uk</a>, a fully certified and independent platform. This platform is trusted across the industry for its ability to conduct impartial and secure prize draws.<br><br>Here's how it works: the draw takes place before the live stream to ensure everything is ready for the announcement. Each entry, whether it's from a free online submission, a postal entry, or a membership, is given equal weight, meaning everyone has a fair chance to win. Our draw process complies with the highest standards, including the Promotional Marketing Rule 8.24 of the CAP Code, guaranteeing an ethical and transparent process every single day.",
          },
          {
            title: 'Am I automatically entered into the draws?',
            text: `Yes! One of the best parts about being a ${this.BUSINESS_NAME} member is that your entries are automatically submitted to all daily prize draws. Depending on the membership tier you choose, you'll receive a set number of daily entries, increasing your chances of winning incredible prizes like cash, luxury vacations, cars, and more.<br>Once you've signed up and your membership is active, you don't have to lift a finger—just sit back, relax, and enjoy the excitement of knowing you're part of every single draw. If you're not a member yet, don't worry; you can still enter individual draws for free online or through our postal entry option.`,
          },
          {
            title: "Can I win if I don't watch the live stream?",
            text: "Absolutely! While we'd love for you to join our live streams and share in the excitement of the winner announcements, it's not a requirement to claim your prize. If you win, our team will contact you directly using the details you've provided.<br>We understand that life gets busy, and you may not always be able to tune in to watch the draw live. That's why we make sure all winners are notified personally so that nobody misses out on their prizes.",
          },
        ],
      }
    },
    HowItWorksSection(): FaqAccordionContent {
      return {
        title: 'How does it work?',
        content: [
          {
            title: `What is ${this.BUSINESS_NAME}?`,
            code: FaqCategories.HowItWorks,
            text: `${this.BUSINESS_NAME} is your all-access pass to daily fun and exclusive perks! It's an entertainment-focused membership that automatically enters you into daily prize draws, where you could win incredible rewards like cash, shiny cars, luxury trips, and more.<br>But that's not all—our members also enjoy a range of monthly perks, from free cinema tickets to discounts at top brands and restaurants. The best part? Each membership tier offers its own unique mix of daily entries and benefits, so there's something for everyone.<br>Curious to know more? Check out all the details on our Membership Page and see what you could be a part of! <br> You can find out more detail on our membership page, just visit <a class='link' target='_blank' href='${this.$router.resolve({ name: 'memberships' }).href}'>${this.$toFullUrl('memberships')}</a>`,
          },
          {
            title: 'How do I know if I have won?',
            code: FaqCategories.HowItWorks,
            text: `Winners are announced daily during our live streams. Tune in live or visit the <a href='${this.$router.resolve({ name: 'results-and-winners' }).href}' class='link'>Winners</a> section of our website to catch up on any missed draws.<br>If you win, we'll contact you directly via the details you've provided. Don't worry - we'll always make sure you know about your win!`,
          },
          {
            title: 'How can I cancel my subscription?',
            code: FaqCategories.HowItWorks,
            text: `Easily! You can cancel your ${this.BUSINESS_NAME} membership at any time. Simply contact us ${this.isCanadianLocale ? 'at' : 'on'} ${this.contactLinkHtml} during our opening hours of 9.00am to 5.30pm Monday to Thursday and Friday 9.00am to 5.00pm. Please rest assured that our team will always get back to you and resolve whatever issue you may have.`,
          },
          {
            title: 'How do I enter the draws?',
            code: FaqCategories.HowItWorks,
            text: `There are three simple ways to enter a draw:<br><br><strong>Free online entry</strong><br>A one-time free entry for everyone. <br><br><strong>Membership</strong><br>Enjoy multiple entries, exclusive perks, and incredible rewards like dining discounts and free cinema tickets — all starting at just ${this.$i18n.n(11.99, 'currency')} a month.<br><br><strong>Postal entry</strong><br>Check out the terms and conditions for details on how to enter by post. Once entered, sit back and relax or tune into our live streams to see if you've won!`,
          },
        ],
      }
    },
    whoAreWeSection(): FaqAccordionContent {
      return {
        title: `Who are ${this.BUSINESS_NAME}?`,
        content: [
          {
            title: `What is ${this.BUSINESS_NAME}?`,
            code: FaqCategories.WhoAreWe,
            text: `${this.BUSINESS_NAME} is your all-access pass to daily fun and exclusive perks! It's an entertainment-focused membership that automatically enters you into daily prize draws, where you could win incredible rewards like cash, shiny cars, luxury trips, and more.<br>But that's not all—our members also enjoy a range of monthly perks, from free cinema tickets to discounts at top brands and restaurants. The best part? Each membership tier offers its own unique mix of daily entries and benefits, so there's something for everyone.<br>Curious to know more? Check out all the details on our Membership Page and see what you could be a part of! <br> You can find out more detail on our membership page, just visit <a class='link' target='_blank' href='${this.$router.resolve({ name: 'memberships' }).href}'>${this.$toFullUrl('memberships')}</a>`,
          },
          {
            title: 'How are good causes chosen?',
            code: FaqCategories.WhoAreWe,
            text: 'Good causes are chosen by our members and our values, ensuring they make a meaningful impact. We focus on supporting up-and-coming athletes and charities that uphold our values, like our recent partnership with the RWW, where we helped provide children in disadvantaged conditions with a special gift from Santa, spreading joy and warmth to those who need it most.',
          },
          {
            title: 'What charities do you work with?',
            code: FaqCategories.WhoAreWe,
            text: 'We are working with the RWW to provide children, living in disadvantaged conditions, with a special gift from Santa, spreading joy and warmth to those who need it most.',
          },
          {
            title: 'Can I submit charities or good causes to be considered?',
            code: FaqCategories.WhoAreWe,
            text: `Of course you can, we are always open to hear about good causes, charities or athletes who need that little extra help to get where they need to be. You can let us know via our email address <a class='link' href='mailto:${this.BUSINESS_EMAIL}' target='_blank'>${this.BUSINESS_EMAIL}</a> or via our phone number <a href="tel:${this.businessPhoneUnformatted}" class="link" target="_blank">${this.BUSINESS_PHONE}</a>.`,
          },
          {
            title: 'Why do you support good causes?',
            code: FaqCategories.WhoAreWe,
            text: `At ${this.BUSINESS_NAME}, we believe in making a positive impact beyond the services we provide. Supporting good causes is at the heart of our mission to create opportunities and uplift communities. By partnering with charities that assist underprivileged children and nurture young athletes, we aim to help those who need it most achieve their potential.`,
          },
          {
            title: 'Where are you based?',
            code: FaqCategories.WhoAreWe,
            text: `We are London based, Our dedicated team is here to support you every step of the way. Whether you have questions about our services or need assistance, you can count on us to provide prompt and helpful responses. <br>To find out more you can contact us via <a class='link' href='mailto:${this.BUSINESS_EMAIL}' target='_blank'>${this.BUSINESS_EMAIL}</a> or give our friendly team a call on <a href="tel:${this.businessPhoneUnformatted}" class="link" target="_blank">${this.BUSINESS_PHONE}</a>.`,
          },
        ],
      }
    },
    winnersAndPrizesSection(): FaqAccordionContent {
      return {
        title: 'Winners and prizes',
        content: [
          {
            title: 'How do I enter the draws?',
            code: FaqCategories.WinnersAndPrizes,
            text: `There are three simple ways to enter a draw:<br><br><strong>Free online entry</strong><br>A one-time free entry for everyone. <br><br><strong>Membership</strong><br>Enjoy multiple entries, exclusive perks, and incredible rewards like dining discounts and free cinema tickets—all starting at just ${this.$i18n.n(11.99, 'currency')} a month.<br><br><strong>Postal entry</strong><br>Check out the terms and conditions for details on how to enter by post. Once entered, sit back and relax or tune into our live streams to see if you've won!`,
          },
          {
            title: 'What types of prizes can I win?',
            code: FaqCategories.WinnersAndPrizes,
            text: `Prizes include everything from cash, shiny cars, and luxury vacations to the latest tech gadgets. Every day, we host a live stream to announce the winners, and we often go the extra mile by adding a little something special to enhance the winner's experience!. Check out the live streams <a class='link' target='_blank' href='${this.$router.resolve({ name: 'results-and-winners' }).href}'>here</a>.`,
          },
          {
            title: 'How do I know if I have won?',
            code: FaqCategories.WinnersAndPrizes,
            text: `Winners are announced daily during our live streams. Tune in live or visit the <a href='${this.$router.resolve({ name: 'results-and-winners' }).href}' class='link'>Winners</a> section of our website to catch up on any missed draws.If you win, we'll contact you directly via the details you've provided. Don't worry - we'll always make sure you know about your win!<br>How are the winners chosen? <br>We use <a class='link' target='_blank' href='//randomdraws.co.uk'>RandomDraws.co.uk</a>, a fully certified and independent platform, to select our winners. This guarantees 100% fairness and transparency.<br>Winners are chosen randomly every day, and the results are announced during our live streams. To keep things fair and compliant, the draw always takes place before the live stream, so we're all set to present the winner on air.<br>Our process meets the highest standards, including the Promotional Marketing Rule 8.24 of the CAP Code—because fairness is non-negotiable!`,
          },
        ],
      }
    },
    entriesAndTicketsSection(): FaqAccordionContent {
      return {
        title: 'Entries & tickets',
        content: [
          {
            title: 'How do I enter the draws?',
            code: FaqCategories.EntriesAndTickets,
            text: `There are three simple ways to enter a draw:<br><br><strong>Free online entry</strong><br>A one-time free entry for everyone. <br><br><strong>Membership</strong><br>Enjoy multiple entries, exclusive perks, and incredible rewards like dining discounts and free cinema tickets—all starting at just ${this.$i18n.n(11.99, 'currency')} a month.<br><br><strong>Postal entry</strong><br>Check out the terms and conditions for details on how to enter by post. Once entered, sit back and relax or tune into our live streams to see if you've won!`,
          },
        ],
      }
    },
    faqPageCategories(): { icon: string; title: string; slug: string }[] {
      return [
        {
          icon: Icons.Building,
          title: `About ${this.BUSINESS_NAME}`,
          slug: FaqSlugs.AboutGoodLifePlus,
        },
        {
          icon: Icons.Star,
          title: 'Membership & Plans',
          slug: FaqSlugs.MembershipPlans,
        },
        {
          icon: Icons.Entries,
          title: 'Entering Draws',
          slug: FaqSlugs.EnteringDraws,
        },
        {
          icon: Icons.Lightening,
          title: 'Instant Win Draws',
          slug: FaqSlugs.InstantWinDraws,
        },
        {
          icon: Icons.EventAvailable,
          title: 'Daily Play & Loyalty',
          slug: FaqSlugs.DailyPlayLoyalty,
        },
        {
          icon: Icons.Winners,
          title: 'Winning & Payouts',
          slug: FaqSlugs.WinningPayouts,
        },
        {
          icon: Icons.Transfer,
          title: 'Deposits & Withdrawals',
          slug: FaqSlugs.DepositsWithdrawals,
        },
        {
          icon: Icons.MoneyBagFill,
          title: 'Credit-Back & Site Credit',
          slug: FaqSlugs.CreditBackSiteCredit,
        },
        {
          icon: Icons.Spaceship,
          title: `Weekly Race & ${this.BUSINESS_NAME} Points`,
          slug: FaqSlugs.WeeklyRaceGoodLifePoints,
        },
        {
          icon: Icons.Gift,
          title: `Rewards`,
          slug: FaqSlugs.Rewards,
        },
        ...(SettingsService.isSectionEnabled('good-causes')
          ? [
              {
                icon: Icons.Diversity,
                title: 'Good Causes & Charity',
                slug: FaqSlugs.GoodCausesCharity,
              },
            ]
          : []),
        {
          icon: Icons.Padlock,
          title: 'Trust & Security',
          slug: FaqSlugs.TrustSecurityPrivacy,
        },
        {
          icon: Icons.Support,
          title: 'Support & Contact',
          slug: FaqSlugs.TroubleshootingSupport,
        },
        {
          icon: Icons.ErrorGoogle,
          title: 'Complaints Policy & Procedure',
          slug: FaqSlugs.ComplaintsPolicy,
        },
        {
          icon: Icons.AccountManage,
          title: 'Account Management',
          slug: FaqSlugs.AccountManagement,
        },
      ].filter(
        (category) =>
          !this.isSubscription ||
          !SUBSCRIPTION_HIDDEN_FAQ_SLUGS.has(category.slug)
      )
    },
    faqPageContent(): FaqPageItem[] {
      return [
        {
          title: `About ${this.BUSINESS_NAME}`,
          code: FaqSlugs.AboutGoodLifePlus,
          content: [
            {
              title: `What is ${this.BUSINESS_NAME} and how does it work?`,
              text: this.isSubscription
                ? `A ${this.BUSINESS_NAME} membership gives you automatic daily entries into exciting prize draws - from cars and holidays to cash and tech - plus monthly member rewards and exclusive partner discounts.`
                : `A ${this.BUSINESS_NAME} membership gives you automatic daily entries into exciting prize draws - from cars and holidays to cash and tech - plus monthly member rewards and exclusive partner discounts.
                You can also enter Instant Win Draws like Mystery Prize Boxes, Prize Spins, or Instant Prizes. Each entry gives you a fair chance to win instantly and also enters you into a main prize draw.`,
            },
            {
              title: `Is ${this.BUSINESS_NAME} really free to enter?`,
              text: ` Yes. Every prize draw has a free entry route, online or by post.
                Most members choose a paid plan because it gives you automatic daily entries, member rewards, and exclusive benefits.`,
            },
            {
              title: `Why should I join ${this.BUSINESS_NAME}?`,
              text: `Because we believe everyone deserves a chance to win.
                ${this.BUSINESS_NAME} combines exciting daily prize draws with real-life rewards, perks, and discounts.
`,
            },
          ],
        },
        {
          title: 'Memberships & Plans',
          code: FaqSlugs.MembershipPlans,
          content: [
            {
              title: 'What’s included in my membership?',
              text: this.isSubscription
                ? `Depending on your plan, you’ll enjoy:<br /><ul class="list-disc"><li class="ml-8">Automatic daily entries into our main prize draws</li><li class="ml-8">Monthly member rewards such as coffee, meal deals, and vouchers for DoorDash and many national chains</li><li class="ml-8">Dining and entertainment discounts</li><li class="ml-8">VIP extras (on higher tiers) like cinema tickets</li><li class="ml-8">Partner perks and seasonal promotions throughout the year</li></ul>`
                : `Depending on your plan, you’ll enjoy:<br /><ul class="list-disc"><li class="ml-8">Automatic daily entries into our main prize draws</li><li class="ml-8">Monthly member rewards such as coffee, meal deals, and vouchers for Deliveroo and many national chains</li><li class="ml-8">Dining and entertainment discounts</li><li class="ml-8">VIP extras (on higher tiers) like cinema tickets</li><li class="ml-8">Partner perks and seasonal promotions throughout the year</li><li class="ml-8">Loyalty Entries and bonus Credit to use on Instant Win Draws</li></ul><br/>Note: Membership entries apply to main prize draws only. Instant Win Draws are separate play-as-you-play draws`,
            },
            {
              title: 'How does billing work?',
              text: '<ul><li class="ml-8"><strong>Monthly plans:</strong> Billed monthly from sign-up. Cancel anytime, no contracts or hidden fees.</li><li class="ml-8"><strong>Annual plans:</strong> Include a 14-day money-back guarantee. You’ll be charged upfront, but if you cancel within 14 days, you’ll receive a full refund.</li></ul>',
            },
            {
              title: 'Can I cancel my membership at any time?',
              text: `Yes. You can cancel anytime by contacting our support team at ${this.contactLinkHtml}. There are no contracts or hidden fees - you’re in control.`,
            },
            {
              title: 'Can I upgrade or downgrade my plan?',
              text: `You can upgrade any time in your <a class="link" href="${this.$router.resolve({ name: 'members-zone' }).href}">Members Zone</a>.<br />To downgrade, please contact our support team by phone (available 9am - 5.30pm weekdays).`,
            },
          ],
        },
        {
          title: 'Entering Draws',
          code: FaqSlugs.EnteringDraws,
          content: [
            {
              title: 'How do I enter the prize draws?',
              text: `There are three ways to enter:<br/><br/><ul><li class="ml-8"><strong>Membership:</strong> Automatic daily entries based on your plan.</li><li class="ml-8"><strong>Free online entry:</strong> a free online route.</li><li class="ml-8"><strong>By post:</strong> enter for free by post.</li></ul><br/>Full details for each route are in our <a
                    href="${this.$router.resolve({ name: 'terms-and-conditions' }).href}" 
                    class="link"
                    target="_blank"
                    >${this.$toFullUrl('terms-and-conditions')}</a>.`,
            },
            {
              title: 'What prizes can I win?',
              text: 'Luxury cars, dream holidays, the latest tech, and cash - plus new surprises added all the time. We run daily prize draws, so there’s always something exciting up for grabs.',
            },
            {
              title: 'How are winners chosen?',
              text: `All final draw winners are selected using <a class='link' target='_blank' href='//randomdraws.co.uk'>RandomDraws.co.uk</a>, an independent and certified system that ensures fairness and transparency.`,
            },
            {
              title: `What happens if a draw doesn't sell out?`,
              text: this.isSubscription
                ? `Every draw has a set close date. Even if all tickets aren’t sold, the draw still goes ahead! A winner is randomly selected from all valid purchased and free entries.`
                : `Every draw has a set close date. Even if all Instant Win tickets aren’t sold, the draw still goes ahead! A winner is randomly selected from all valid purchased and free entries.`,
            },
            ...(this.isSubscription
              ? []
              : [
                  {
                    title: `When are the livestreams held?`,
                    text: `<ul class="list-disc"><li class="ml-8">Monday to Friday: 7pm</li><li class="ml-8">Saturday & Sunday: 12pm.</li></ul><br/><i>Excluding Bank Holidays*</i><br/>Watch live on YouTube or Facebook, or catch up later on the website results page.`,
                  },
                ]),
            {
              title: `Can I watch past draws?`,
              text: `Yes. All past draws and winner announcements are available on our YouTube and Facebook channels or on our website results page.`,
            },
          ],
        },
        {
          title: 'Instant Win Draws - How They Work',
          code: FaqSlugs.InstantWinDraws,
          content: [
            {
              title: 'What are Instant Win Draws?',
              text: `Instant Win Draws give you a fun, interactive way to play - each ticket is a chance to win instantly and also enters you into the main prize draw.<br /><br/>All Instant Win Draws share the same fair system:<ul class="list-disc"><li class="ml-8">Each ticket is entered into the main prize draw.</li><li class="ml-8">Instant prizes are assigned to specific ticket numbers at the draw opening.</li><li class="ml-8">Each ticket number is randomly assigned at purchase.</li><li class="ml-8">You can see which prizes remain and which ticket numbers are assigned to them.</li><li class="ml-8">Animations are for entertainment only.</li><ul><br/>Instant results are immediate, and main prizes are drawn later.
              You can enter all Instant Win Main Draws via our postal entry route if you choose.`,
            },
            {
              title: 'What are Prize Spins?',
              text: `Each entry gives you the chance to win a prize instantly and you also get entered into the main prize draws for each entry you buy!`,
            },
            {
              title: 'What are Instant Packs?',
              text: 'A fun way to play, instant prize draws. Each entry goes into the main prize draw. Instant prize results are decided immediately and are revealed via a card pack animation for entertainment.',
            },
            {
              title: 'What is a Mystery Prize Box?',
              text: 'A fun way to play, instant prize draws. Each entry goes into the main prize draw. Instant prize results are decided immediately and are revealed by a Mystery Box animation for entertainment.',
            },
            {
              title: 'What are Instant Prizes?',
              text: `A fun way to play, instant prize draws. Each entry goes into the main prize draw. Instant prize results are decided and presented immediately.`,
            },
            {
              title: 'Are the draws fair?',
              text: `Yes. All Instant Prize Draws use a randomised algorithm to issue ticket numbers post-purchase, ensuring fair play for every participant.`,
            },
          ],
        },
        {
          title: 'Daily Play & Loyalty',
          code: FaqSlugs.DailyPlayLoyalty,
          content: [
            {
              title: 'What is the Daily Game - Spin & Win and how do I play?',
              text: `The Daily Game - Spin & Win is a free-to-play instant game available every day in your <a class="link" href="${this.$router.resolve({ name: 'members-zone' }).href}">Members Zone</a>. Play for a chance to win bonus site credits - just for logging in.`,
            },
            {
              title: 'What are Loyalty Entries?',
              text: `Loyalty Entries are extra entries we give to long-term members for the main prize draws.
                <strong>With an active subscription</strong>, you’ll keep building Loyalty Entries - but if you cancel, these entries are removed.`,
            },
          ],
        },
        {
          title: 'Winning & Payouts',
          code: FaqSlugs.WinningPayouts,
          content: [
            {
              title: `How will I know if I’ve won?`,
              text: `Winners are announced daily on our livestreams and contacted directly via email, phone or SMS.
                You can also check your Member Dashboard for prize updates.`,
            },
            {
              title: 'What happens when I win?',
              text: this.isSubscription
                ? 'Cash prizes are automatically added to your balance.<br/>Physical prizes (like cars or tech) appear in your dashboard, where you can claim the prize or take a cash alternative.<br/>Once you’ve made your choice, our team confirms delivery or updates your balance.'
                : 'Cash or credit prizes are automatically added to your balance.<br/>Physical prizes (like cars or tech) appear in your dashboard, where you can claim the prize, take a cash alternative, or claim site credit.<br/>Once you’ve made your choice, our team confirms delivery or credits your account.',
            },
            {
              title: 'If you believe you’ve won and haven’t heard from us:',
              text: this.isSubscription
                ? `Please contact us at <a class="link" href="mailto:${this.BUSINESS_WINNERS_EMAIL}">${this.BUSINESS_WINNERS_EMAIL}</a>.`
                : `<ul class="list-disc"><li class="ml-8">Instant Prizes: <a class="link" href="mailto:${this.BUSINESS_INSTANT_WINS_EMAIL}">${this.BUSINESS_INSTANT_WINS_EMAIL}</a></li><li class="ml-8">Main Draw Prizes: <a class="link" href="mailto:${this.BUSINESS_WINNERS_EMAIL}">${this.BUSINESS_WINNERS_EMAIL}</a></li></ul>`,
            },
            {
              title:
                'Can I choose a cash alternative instead of a physical prize?',
              text: 'Yes. For most major prizes, you’ll be given the option to take the physical item or a cash alternative.',
            },
          ],
        },
        {
          title: 'Deposits & Withdrawals',
          code: FaqSlugs.DepositsWithdrawals,
          content: [
            {
              title: 'How do I make a deposit?',
              text: `You can deposit funds in your <a class="link" href="${this.$router.resolve({ name: 'members-zone' }).href}">Members Zone</a>, or purchase entries using a debit or credit card, Google Pay or Apple Pay.
                Cash winnings or promotional credits are automatically added to your balance.`,
            },
            {
              title: 'Are there limits or fees for deposits or withdrawals?',
              text: `There are no fees from our side. The minimum withdrawal amount is ${this.$i18n.n(this.getCurrencyValue(10), 'currency-short')}.`,
            },
            {
              title: 'How long do withdrawals take to process?',
              text: `Withdrawals are processed securely within 7 days of your request.
                Large prizes may take longer due to validation. Once processed, funds appear in your bank account shortly after.`,
            },
          ],
        },
        {
          title: 'Credit-Back & Site Credit',
          code: FaqSlugs.CreditBackSiteCredit,
          content: [
            {
              title: 'What is Cashback and how does it work?',
              text: `Cashback gives you a percentage back on eligible card-funded game purchases.
Cashback is available to higher-tier members or during special promotions and is added to your ${this.BUSINESS_NAME} account as credit.
Cashback does not apply to wallet spend funded by deposits, bonuses, or existing credits.`,
            },
            {
              title: `Do my credits expire?`,
              text: `Promotional and bonus credits may have expiry dates - please check <a
                    href="${this.$router.resolve({ name: 'terms-and-conditions' }).href}" 
                    class="link"
                    target="_blank"
                    >${this.$toFullUrl('terms-and-conditions')}</a>.`,
            },
          ],
        },
        {
          title: `Weekly Race & ${this.BUSINESS_NAME} Points`,
          code: FaqSlugs.WeeklyRaceGoodLifePoints,
          content: [
            {
              title: 'What is the Weekly Race and how does it work?',
              text: `The Weekly Race is a leaderboard that showcases players who’ve earned the most ${this.BUSINESS_NAME} Points during a set period.`,
            },
            {
              title: `What are ${this.BUSINESS_NAME} Points and how do I level up?`,
              text: `${this.BUSINESS_NAME} Points track your activity and loyalty. You earn points by staying subscribed, making purchases, and engaging daily.`,
            },
            {
              title: 'What benefits come with higher levels?',
              text: `Higher levels unlock better rewards - including enhanced cashback rates, exclusive prizes, and bonuses.`,
            },
          ],
        },
        ...(SettingsService.isSectionEnabled('rewards')
          ? [
              {
                title: `Rewards`,
                code: FaqSlugs.Rewards,
                content: [
                  {
                    title: 'What are points and how do they work?',
                    text: this.isSubscription
                      ? `Points are your new flexible rewards currency.<br>Instead of fixed monthly perks, you now receive points with your membership - and choose how to spend them.<br><br>Use your points on things like draw entries or vouchers.<br><br>The more points you have, the more ways you can play.`
                      : `Points are your new flexible rewards currency.<br>Instead of fixed monthly perks, you now receive points with your membership - and choose how to spend them.<br><br>Use your points on things like draw entries, Instant Wins, or vouchers.<br><br>The more points you have, the more ways you can play.`,
                  },
                  {
                    title: `How many points do I get with my membership?`,
                    text: this.isSubscription
                      ? `Each plan comes with a monthly Points allowance:<br/><br/><ul class="list-disc"><li class="ml-8"><strong>Standard:</strong> 1,000 Points</li><li class="ml-8"><strong>Premium:</strong> 2,500 Points</li><li class="ml-8"><strong>VIP:</strong> 5,000 Points</li><li class="ml-8"><strong>Legend:</strong> 10,000 Points</li></ul><br/>Points are added to your account automatically as part of your active paid membership.`
                      : `Each plan comes with a monthly Points allowance:<br/><br/><ul class="list-disc"><li class="ml-8"><strong>Standard:</strong> 500 Points</li><li class="ml-8"><strong>Premium:</strong> 1,500 Points</li><li class="ml-8"><strong>VIP:</strong> 3,000 Points</li></ul><br/>Points are added to your account automatically as part of your active paid membership.`,
                  },
                  ...(this.isSubscription
                    ? []
                    : [
                        {
                          title: 'Do I earn points when I spend?',
                          text: `Yes! You'll earn 25 points for every ${this.$i18n.n(1, 'currency-short')} you spend (excluding all credit).<br><br>It's our way of giving you even more value while you play.`,
                        },
                      ]),
                  ...(this.isSubscription
                    ? []
                    : [
                        {
                          title: 'Do I get bonus points on my first deposit?',
                          text: `Yes, as long as your first deposit is over ${this.$i18n.n(this.getCurrencyValue(5), 'currency-short')} you will receive 500 points on the first ${this.$i18n.n(this.getCurrencyValue(5), 'currency-short')}, you will subsequently receive 25 points per ${this.$i18n.n(this.getCurrencyValue(1), 'currency-short')} thereafter.`,
                        },
                      ]),
                  {
                    title: 'What can I spend my points on?',
                    text: this.isSubscription
                      ? `You're in control. You can redeem points for:<br/><br/><ul class="list-disc"><li class="ml-8">Draw entries i.e 100 Entries to C63 draw.</li><li class="ml-8">Vouchers (e.g. ${this.$i18n.n(20, 'currency-short')} rewards and more).</li></ul><br/>We're always adding new options, so there's plenty to explore.`
                      : `You're in control. You can redeem points for:<br/><br/><ul class="list-disc"><li class="ml-8">Draw entries i.e 100 Entries to C63 draw.</li><li class="ml-8">Instant Win games (Packs, Boxes,Spins and Instant Wins).</li><li class="ml-8">Vouchers (e.g. ${this.$i18n.n(20, 'currency-short')} rewards and more).</li></ul><br/>We're always adding new options, so there's plenty to explore.`,
                  },
                  {
                    title: 'How do I redeem my points?',
                    text: `Just head to the <a
                    href="${this.$router.resolve({ name: 'rewards' }).href}" 
                    class="link"
                    target="_blank"
                    >Rewards page</a>, choose your reward, and redeem using your points.<br><br>It's quick, simple, and entirely up to you how you spend them.`,
                  },
                  {
                    title: 'When will my points appear?',
                    text: this.isSubscription
                      ? `Points are added instantly after eligible purchases.<br><br>If you've just upgraded your membership, your new monthly points allowance will update accordingly.`
                      : `Points are added instantly after eligible actions like deposits or purchases.<br><br>If you've just upgraded your membership, your new monthly points allowance will update accordingly.`,
                  },
                  {
                    title: 'What happens if I upgrade my plan?',
                    text: `When you upgrade, your monthly points allowance increases to match your new tier.<br>Any future points you receive will reflect your upgraded plan.`,
                  },
                  {
                    title: 'Do points expire?',
                    text: `Points may expire depending on your membership status and activity.<br><br>We'll always show your balance clearly in your account so you can use them in time. Points expire after 120 days.`,
                  },
                  {
                    title: 'Can I exchange points for cash?',
                    text: `No - Points can't be withdrawn as cash.<br><br>They're designed to be used on rewards, entries, and experiences across ${this.BUSINESS_NAME}.`,
                  },
                ],
              },
            ]
          : []),
        ...(SettingsService.isSectionEnabled('good-causes')
          ? [
              {
                title: 'Good Causes & Charity',
                code: FaqSlugs.GoodCausesCharity,
                content: [
                  {
                    title: `Does ${this.BUSINESS_NAME} support good causes or charities?`,
                    text: `Yes. We donate 10% of net proceeds from all new Standard Memberships to our charity partners.`,
                  },
                  {
                    title: 'Which charities do you support?',
                    text: 'We focus on charities that support young people across the UK, including:<br/><ul class="list-disc"><li class="ml-8">Legacy Youth Zone Croydon</li><li class="ml-8">RWW Foundation</li></ul><br/>We also support young upcoming athletes on their journey to the top.',
                  },
                ],
              },
            ]
          : []),
        {
          title: 'Trust & Security',
          code: FaqSlugs.TrustSecurityPrivacy,
          content: [
            {
              title: 'Are your draws fair?',
              text: `Yes. All main draws are carried out using independent random draw software. Every participant - paid or free - has an equal chance to win. All draws are independently verified for fairness.`,
            },
            {
              title: 'How is my data handled?',
              text: 'Your information is handled securely and in full compliance with GDPR. We only use your data to manage entries, contact winners, and provide member services - never shared or sold without consent.',
            },
            {
              title: `How can I tell if a message or call is really from ${this.BUSINESS_NAME}?`,
              text: 'We’ll never ask for your banking passwords or PIN. Winners are always announced via our official livestreams and contacted through verified channels.',
            },
            {
              title: `What should I do if I suspect a scam or fake page?`,
              text: `Do not click links or share details. Contact us immediately ${!this.isSubscription ? `via our official number: <a href="tel:${this.businessPhoneUnformatted}" class="link" target="_blank">${this.BUSINESS_PHONE}</a>` : ''}.`,
            },
          ],
        },
        {
          title: 'Support & Contact',
          code: FaqSlugs.TroubleshootingSupport,
          content: [
            {
              title: 'How do I contact customer support?',
              text: `<ul>${!this.isSubscription ? `<li class="ml-8"><strong>Phone:</strong> <a href="tel:${this.businessPhoneUnformatted}" class="link" target="_blank">${this.BUSINESS_PHONE}</a></li>` : ''}<li class="ml-8"><strong>Email:</strong> <a class='link' href='mailto:${this.BUSINESS_EMAIL}' target='_blank'>${this.BUSINESS_EMAIL}</a></li></ul>`,
            },
            {
              title: 'I can’t log in - what should I do?',
              text: 'Reset your password first. If you still can’t access your account, contact our support team for help.',
            },
            {
              title: `My reward code isn’t working -  what should I do?`,
              text: 'Check that it hasn’t expired and meets the listed terms. If you still can’t use it, our support team will fix it for you.',
            },
            {
              title: `I didn’t receive my monthly reward -  what now?`,
              text: 'Check your Benefits page. If it’s not there, get in touch and we’ll make it right.',
            },
          ],
        },
        {
          title: 'Complaints & Policy Procedure',
          code: FaqSlugs.ComplaintsPolicy,
          content: [
            {
              title: 'How can I make a complaint?',
              text: `Email <a class='link' href='mailto:${this.BUSINESS_COMPLAINTS_EMAIL}' target='_blank'>${this.BUSINESS_COMPLAINTS_EMAIL}</a> with the subject line “Official Complaint,” or write to:<br/><br/><strong>${this.BUSINESS_REGISTERED_NAME} – Complaints Team</strong><br/><address class="inline">${this.BUSINESS_ADDRESS}</address>`,
            },
            {
              title: 'Our Process',
              text: '<ul class="list-disc"><li class="ml-8"><strong>Acknowledgement:</strong> Within 5 working days.</li><li class="ml-8"><strong>Investigation:</strong> Thorough and fair review.</li><li class="ml-8"><strong>Response:</strong> Full written reply within 30 days.</li></ul><br/>If you’re not satisfied, you can request an internal review by a senior team member. We handle all complaints fairly and use feedback to improve our service.',
            },
          ],
        },
        {
          title: 'Account Management',
          code: FaqSlugs.AccountManagement,
          content: [
            {
              title: 'How can I update my personal or payment details?',
              text: `You can update your details anytime in the <a class="link" href="${this.$router.resolve({ name: 'members-zone' }).href}">Members Zone</a>.`,
            },
            {
              title: 'How do I change my phone number?',
              text: `Edit your contact info in the <a class="link" href="${this.$router.resolve({ name: 'members-zone' }).href}">Members Zone</a>. For security, you may need to verify changes.`,
            },
            {
              title: 'Can I delete or deactivate my account?',
              text: `Yes. Contact our support team by phone or email and they’ll help close or deactivate your account securely.`,
            },
            {
              title: 'How can I request my data to be removed?',
              text: `You can request full deletion of your personal data by:<br/><ul class="list-disc">${!this.isSubscription ? `<li class="ml-8">Calling our customer service team on <a href="tel:${this.businessPhoneUnformatted}" class="link" target="_blank">${this.BUSINESS_PHONE}</a>, or</li>` : ''}<li class="ml-8">Emailing <a class='link' href='mailto:privacy@goodlifeplus.co.uk' target='_blank'>privacy@goodlifeplus.co.uk</a> with the subject line “Data Removal Request.”</li></ul><br/>Once verified, we’ll delete your information securely in line with GDPR regulations. Some data may be retained for legal or financial record-keeping as required by law.
                Your information is always handled securely and never shared without consent.`,
            },
          ],
        },
      ].filter(
        (category) =>
          !this.isSubscription ||
          !SUBSCRIPTION_HIDDEN_FAQ_SLUGS.has(category.code)
      )
    },
    dailySpins(): Array<AccordionContent> {
      return [
        {
          title: 'What are Daily Spins?',
          text: 'Daily Spins is a free-to-play feature that gives you <strong>3 spins each day</strong> to win <strong>Site Credit</strong>.',
        },
        {
          title: 'Do I need to deposit to play?',
          text: 'No. Daily Spins is completely free to play.',
        },
        {
          title: 'What can I win?',
          text: '<strong>Site Credit only.</strong> No cash prizes are available in Daily Spins.',
        },
        {
          title: 'How does it work?',
          text: 'Use your daily spins to collect symbols and fill the reward buckets to earn Site Credit.',
        },
        {
          title: 'How long do I have to complete Daily Spins?',
          text: 'You have <strong>7 days, from Monday to Sunday</strong>, to fill the buckets. Progress resets every Monday morning, so logging in daily gives you the best chance to complete them.',
        },
        {
          title: `Does my activity on ${this.BUSINESS_NAME} affect Daily Spins?`,
          text: `Yes. The more you play or make purchases on ${this.BUSINESS_NAME}, the <strong>more generous your Daily Spins experience becomes</strong>, including unlocking <strong>better symbols and rewards</strong>.`,
        },
        {
          title: 'Can I withdraw Daily Spins winnings?',
          text: `No. All Daily Spins rewards are <strong>Site Credit</strong> and must be used on ${this.BUSINESS_NAME}.`,
        },
      ]
    },
  },

  actions: {
    prizeSpecificFaq(prize: Prize): FaqAccordionContent {
      return {
        title: '',
        content: [
          {
            title: 'How do I win these prizes?',
            text: `It's easy, you can enter by the following methods: <br>Free online entry: A one-time free entry for everyone. <br>Membership: Enjoy multiple entries, exclusive perks, and incredible rewards like dining discounts and free cinema tickets—all starting at just ${this.$i18n.n(11.99, 'currency')} a month.<br>Postal entry: Check out the terms and conditions for details on how to enter by post.<br>Each entry is of equal weight in the draw. <br>Once you have entered, sit back and relax or enjoy one of our daily live streams!`,
          },
          {
            title: `Is this the actual ${prize.categories.includes(PrizeCategories.DreamCars) ? 'car' : 'item'} you are giving away?`,
            text: `The ${prize.categories.includes(PrizeCategories.DreamCars) ? 'car' : 'item'} shown is often the actual prize; however, this depends on the specific draw. In some cases, a similar ${prize.categories.includes(PrizeCategories.DreamCars) ? 'car' : 'item'} of equal or greater value and specification may be provided. Full details are outlined in the terms and conditions of each competition. Please refer to the specific draw's terms for clarity.`,
          },
          {
            title: 'Am I automatically entered?',
            text: "If you have an active membership, your entries will automatically be applied to all live draws, so sit back and relax! Or join the live streams, we stream our winner reveals on a daily basis, the streams are always fun and quite often we give away an additional prize to the people watching the stream (just don't tell everyone!)",
          },
          {
            title: 'Can I take a cash alternative?',
            text: "We sometimes offer a cash alternative, however this depends largely on the type of the prize! You can always find out about this in the prize's terms & conditions.",
          },
          {
            title: 'Can I cancel anytime?',
            text: 'Our monthly plans are billed monthly and you can cancel anytime, and our annual plans have a 14 day money back guarantee. Making our membership absolutely worry free!',
          },
        ],
      }
    },
    instantWinsFaq(instantWinDraw: InstantWinDraw): FaqAccordionContent {
      return {
        title: this.$i18n.t('section.faqs.heading'),
        content: [
          {
            title: 'What are Instant Prizes?',
            text: `Instant Prizes are draws that give you not only the chance to win the closing end prize, but also to win cash and other prizes instantly! This is a great way to get a bit of entertainment in alongside your ${this.BUSINESS_NAME} subscription.
            <br>
            <br>
            With every entry purchased, not only will you be in with the chance to win the random end prize draw, you will also receive an instant prize for free, so <strong>every ticket wins</strong>!`,
          },
          {
            title: `What happens if a draw doesn't sell out?`,
            text: 'We will always honour the entries and perform the end prize draw shortly after the close date. On the following Monday we will announce the winner of the end draw prize in our live stream.',
          },
          {
            title: 'Can I win more than one prize?',
            text: 'Absolutely, you have every chance of winning more than one prize, even if you buy a single entry you can win twice, you can win an instant prize and the end prize!',
          },
          {
            title: 'If I win how long will it take to get my prize?',
            text: `
              Most of the prizes are delivered instantly, you don't need to lift a finger!
              <br>
              <br>
              If you're a lucky person and win a cash prize, you will just need to email us at <a
                    class="link"
                    href="mailto:${this.BUSINESS_INSTANT_WINS_EMAIL}"
                    target="_blank"
                    >${this.BUSINESS_INSTANT_WINS_EMAIL}</a
                  > we will be in touch as soon as possible!
              <br>
              <br>
              Entries you have won to future prize draws will automatically be added to your account and you can see them in the Members-zone. All you have to do to check them is set a password after your purchase to sign in, or click the link in the email we send you to set your password and access the Members-zone to see your entries into future prize draws!
              <br>
              <br>
              Should you win the end prize draw, we'll be in touch within 5 working days.
            `,
          },
          {
            title: `I have a question that isn't answered here! Help?`,
            text: `You can always reach out to our lovely customer service team, they can be reached via email on <a
                    class="link"
                    href="mailto:${this.BUSINESS_INSTANT_WINS_EMAIL}"
                    target="_blank"
                    >${this.BUSINESS_INSTANT_WINS_EMAIL}</a
                  >`,
          },
          {
            title: 'When do I get my ticket number?',
            text: 'Your ticket number(s) will be shown after the checkout process as part of your order and will be available in your Members-zone!',
          },
          {
            title: `How will I know if I've won?`,
            text: 'You will be able to reveal your instant prize results instantly after you have completed your order. You will be contacted directly if you win the end prize.',
          },
          {
            title: 'What happens if I win entries?',
            text: 'Entries you have won to future prize draws will automatically be added to your account and you can see them in the Members-zone. All you have to do to check them is set a password after your purchase to sign in, or click the link in the email we send you! <br><br>Your entries will automatically be applied to the specific draw!',
          },
          {
            title: 'Competition Rules',
            text: `
              You will be allocated ticket number(s) at random when your order is confirmed, when you will be able
              to see your ticket number(s) and instant prize results. Your purchased ticket numbers will also be
              available in the Members-zone. Your ticket number(s) will reveal your instant prize results as well as be
              your ticket number(s) for the end prize draw. The total amount of entries for this competition is ${this.$i18n.n(instantWinDraw.total_tickets, 'number')}.
              The end prize draw will take place on the ${this.$i18n.d(instantWinDraw.main_prize.close_at.toJSDate(), 'full')} regardless of the number of tickets sold in this
              instant prize competition.
              <br>
              <br>
              To enter the end prize draw for free send an unenclosed postcard stating your: Full Name, Email
              Address, Phone Number, Full Address, Draw Name. All entries must be sent to <address class="inline-block">${this.BUSINESS_REGISTERED_NAME} ${this.BUSINESS_ADDRESS}</address>, entries must be received 24 hours before the draw
              close date.
              <br>
              <br>
              Please ensure you have read and agree to our terms and conditions before entering by post or online.
              If your entries do not comply with the terms they will not be included in the draw. Please note, when
              making a purchase and if you don't have an account with ${this.BUSINESS_NAME}, an account will be created for
              you automatically, when this happens you will have the option to set a password right away or via the
              email we'll send you.
              <br>
              <br>
              You can find us on Facebook and Instagram to stay up to date with the latest about ${this.BUSINESS_NAME}!
              <br>
              <br>
              You can find the full terms & conditions for instant prize competitions at <a 
                href="${this.$router.resolve({ name: 'instant-win-terms-and-conditions', params: { instantWinCode: instantWinDraw.code } }).href}"
                class="link"
                target="_blank">${instantWinDraw.title} Terms & Conditions</a>.
            `,
          },
        ],
      }
    },
    mysteryBoxesFaq(instantWinDraw: InstantWinDraw): FaqAccordionContent {
      return {
        title: this.$i18n.t('section.faqs.heading'),
        content: [
          {
            title: 'What is a Mystery Prize Box?',
            text: `A Mystery Prize Box is a fun bonus you receive with every entry you purchase to the end draw. Each box gives you the chance to win instant cash, prizes, and a spot in the grand prize draw — all at no extra cost. You'll know what you've won instantly, and since you get a box with every entry, the more you enter, the more chances you have to win!`,
          },
          {
            title: `Can I win more than one prize?`,
            text: 'Absolutely, you have every chance of winning more than one prize, even if you buy a single entry you can win twice. You can win the end prize along with anything inside your Mystery Prize Box!',
          },

          {
            title: `How will I know if I've won?`,
            text: 'You will be able to reveal your Mystery Prize Box results instantly after you have completed your order. You will be contacted directly if you win the end prize.',
          },
          {
            title: `What happens if a draw doesn't sell out?`,
            text: 'We will always honour the entries and perform the end prize draw shortly after the close date. On the following Monday we will announce the winner of the end draw prize in our live stream.',
          },
          {
            title: 'If I win how long will it take to get my prize?',
            text: `Most of the prizes are delivered instantly, you don't need to lift a finger!
              <br><br>
              If you're a lucky person and win a cash prize, you will just need to email us at <a href="mailto:${this.BUSINESS_INSTANT_WINS_EMAIL}">${this.BUSINESS_INSTANT_WINS_EMAIL}</a> we will be in touch as soon as possible!
              <br><br>
              Entries you have won to future prize draws will automatically be added to your account and you can see them in the Members-zone. All you have to do to check them is set a password after your purchase to sign in, or click the link in the email we send you to set your password and access the Members-zone to see your entries into future prize draws!
              <br><br>
              Should you win the end prize draw, we'll be in touch within 5 working days.
              `,
          },
          {
            title: `I have a question that isn't answered here! Help?`,
            text: `You can always reach out to our lovely customer service team, they can be reached via email on <a href="mailto:${this.BUSINESS_INSTANT_WINS_EMAIL}">${this.BUSINESS_INSTANT_WINS_EMAIL}</a>`,
          },
          {
            title: `When do I get my ticket number?`,
            text: `Your ticket number(s) will be shown after the checkout process as part of your order and will be available in your Members-zone!`,
          },
          {
            title: 'Competition Rules',
            text: `
              You will be allocated ticket number(s) at random when your order is confirmed, when you will be able to see your ticket number(s) and Mystery Prize Box results. Your purchased ticket numbers will also be available in the Members-zone. Your ticket number(s) will reveal your Mystery Prize Box results as well as be your ticket number(s) for the end prize draw. The total amount of entries for this competition is ${this.$i18n.n(instantWinDraw.total_tickets, 'number')}. The end prize draw will take place on the ${this.$i18n.d(instantWinDraw.main_prize.close_at.toJSDate(), 'full')} regardless of the number of tickets sold in this Mystery Prize Box competition.
              <br>
              <br>
              To enter the end prize draw for free send an unenclosed postcard stating your: Full Name, Email
              Address, Phone Number, Full Address, Draw Name. All entries must be sent to <address class="inline-block">${this.BUSINESS_REGISTERED_NAME} ${this.BUSINESS_ADDRESS}</address>, entries must be received 24 hours before the draw
              close date.
              <br>
              <br>
              Please ensure you have read and agree to our terms and conditions before entering by post or online.
              If your entries do not comply with the terms they will not be included in the draw. Please note, when
              making a purchase and if you don't have an account with ${this.BUSINESS_NAME}, an account will be created for
              you automatically, when this happens you will have the option to set a password right away or via the
              email we'll send you.
              <br>
              <br>
              You can find us on Facebook and Instagram to stay up to date with the latest about ${this.BUSINESS_NAME}!
              <br>
              <br>
              You can find the full terms & conditions for instant prize competitions at <a 
                href="${this.$router.resolve({ name: 'instant-win-terms-and-conditions', params: { instantWinCode: instantWinDraw.code } }).href}"
                class="link"
                target="_blank">${instantWinDraw.title} Terms & Conditions</a>.
            `,
          },
        ],
      }
    },
    instantSpinsFaq(instantWinDraw: InstantWinDraw): FaqAccordionContent {
      return {
        title: this.$i18n.t('section.faqs.heading'),
        content: [
          {
            title: 'What is a Prize Spin?',
            text: `A Prize Spin is a fun bonus you receive with every entry you purchase to the end draw. Each spin gives you the chance to win instant cash, prizes, and a spot in the grand prize draw — all at no extra cost. You'll know what you've won instantly, and since you get a spin with every entry, the more you enter, the more chances you have to win!`,
          },
          {
            title: `Can I win more than one prize?`,
            text: 'Absolutely, you have every chance of winning more than one prize, even if you buy a single entry you can win twice. You can win the end prize along with anything inside your Prize Spin!',
          },

          {
            title: `How will I know if I've won?`,
            text: 'You will be able to reveal your Prize Spin results instantly after you have completed your order. You will be contacted directly if you win the end prize.',
          },
          {
            title: `What happens if a draw doesn't sell out?`,
            text: 'We will always honour the entries and perform the end prize draw shortly after the close date. On the following Monday we will announce the winner of the end draw prize in our live stream.',
          },
          {
            title: 'If I win how long will it take to get my prize?',
            text: `Most of the prizes are delivered instantly, you don't need to lift a finger!
              <br><br>
              If you're a lucky person and win a cash prize, you will just need to email us at <a href="mailto:${this.BUSINESS_INSTANT_WINS_EMAIL}">${this.BUSINESS_INSTANT_WINS_EMAIL}</a> we will be in touch as soon as possible!
              <br><br>
              Entries you have won to future prize draws will automatically be added to your account and you can see them in the Members-zone. All you have to do to check them is set a password after your purchase to sign in, or click the link in the email we send you to set your password and access the Members-zone to see your entries into future prize draws!
              <br><br>
              Should you win the end prize draw, we'll be in touch within 5 working days.
              `,
          },
          {
            title: `I have a question that isn't answered here! Help?`,
            text: `You can always reach out to our lovely customer service team, they can be reached via email on <a href="mailto:${this.BUSINESS_INSTANT_WINS_EMAIL}">${this.BUSINESS_INSTANT_WINS_EMAIL}</a>`,
          },
          {
            title: `When do I get my ticket number?`,
            text: `Your ticket number(s) will be shown after the checkout process as part of your order and will be available in your Members-zone!`,
          },
          {
            title: 'Competition Rules',
            text: `
              You will be allocated ticket number(s) at random when your order is confirmed, when you will be able to see your ticket number(s) and Prize Spin results. Your purchased ticket numbers will also be available in the Members-zone. Your ticket number(s) will reveal your Prize Spin results as well as be your ticket number(s) for the end prize draw. The total amount of entries for this competition is ${this.$i18n.n(instantWinDraw.total_tickets, 'number')}. The end prize draw will take place on the ${this.$i18n.d(instantWinDraw.main_prize.close_at.toJSDate(), 'full')} regardless of the number of tickets sold in this Prize Spin competition.
              <br>
              <br>
              To enter the end prize draw for free send an unenclosed postcard stating your: Full Name, Email
              Address, Phone Number, Full Address, Draw Name. All entries must be sent to <address class="inline-block">${this.BUSINESS_REGISTERED_NAME} ${this.BUSINESS_ADDRESS}</address>, entries must be received 24 hours before the draw
              close date.
              <br>
              <br>
              Please ensure you have read and agree to our terms and conditions before entering by post or online.
              If your entries do not comply with the terms they will not be included in the draw. Please note, when
              making a purchase and if you don't have an account with ${this.BUSINESS_NAME}, an account will be created for
              you automatically, when this happens you will have the option to set a password right away or via the
              email we'll send you.
              <br>
              <br>
              You can find us on Facebook and Instagram to stay up to date with the latest about ${this.BUSINESS_NAME}!
              <br>
              <br>
              You can find the full terms & conditions for instant prize competitions at <a 
                href="${this.$router.resolve({ name: 'instant-win-terms-and-conditions', params: { instantWinCode: instantWinDraw.code } }).href}"
                class="link"
                target="_blank">${instantWinDraw.title} Terms & Conditions</a>.
            `,
          },
        ],
      }
    },
  },
})
